/**
 * Low-level HTTP bridge to the Topaz SigWeb service.
 *
 * SigWeb is vendor software installed on the *workstation* (not in the browser)
 * that exposes the attached signature pad over a loopback HTTP(S) listener.
 * This mirrors the two local-service bridges the app already ships:
 * the NOAH client (`services/noah.ts` → https://localhost:8090) and the
 * Dynamsoft scanner service (`features/dynamic-web-twain`).
 *
 * EVERY SigWeb wire call lives in this file. The provider, the UI and the tests
 * all go through the small typed surface below, so if a firmware/SDK revision
 * moves an endpoint there is exactly one file to correct.
 *
 * Notes on the transport:
 * - **The TLS listener must be addressed as `tablet.sigwebtablet.com`, NOT as
 *   `localhost`.** Topaz ships a publicly-trusted certificate issued to that
 *   hostname, and publishes it in public DNS as an A record for 127.0.0.1 —
 *   the request never leaves the machine, but the name matches the cert. Using
 *   `https://localhost:47290` presents that same certificate under the wrong
 *   name, so Chrome fails the handshake (`ERR_CERT_COMMON_NAME_INVALID`) and
 *   `fetch` rejects with a bare `TypeError`. That looks exactly like "SigWeb
 *   isn't installed", which is what the dialog then (wrongly) reported on
 *   workstations where SigWeb was installed and running perfectly.
 * - The app is served over HTTPS in every deployed environment, so the
 *   plaintext port (47289) is unusable there: browsers block http://
 *   subresources from an https:// page. It is still probed under a plain-http
 *   dev server.
 * - SigWeb aggressively caches GETs; every read carries a `noCache` param.
 * - Every request is time-boxed with AbortController. A pad that is unplugged
 *   mid-session must never hang the signing dialog.
 *
 * WIRE FORMAT — verified against Topaz's own SigWebTablet wrapper, not guessed:
 * - **Reading** a property is `GET <Prop>?noCache=…`, returning plain text.
 * - **Writing** one is `POST <Prop>/<value>` — the value is a PATH SEGMENT,
 *   with no body and no headers. A write sent as `GET <Prop>?<Prop>=<value>`
 *   is silently treated as a READ: it returns 200 with the current value and
 *   changes nothing, so a wrong write looks like a working one.
 * - Property names are not uniformly prefixed: it is `ImageXSize` but plain
 *   `JustifyMode`, and the point count is `TotalPoints`. There is NO
 *   `ImageTransparency` property; asking for one 404s.
 * - `SigImage/1` returns image BYTES, not base64 text.
 */
import { type SignatureDeviceFailure } from "./signature-provider.type";

/**
 * Topaz's canonical TLS endpoint. `tablet.sigwebtablet.com` is a public DNS
 * name resolving to 127.0.0.1, and is the CN on the certificate the SigWeb
 * installer registers — so this is the ONLY origin that can complete a TLS
 * handshake from an https:// page on a stock install.
 */
export const SIGWEB_TLS_BASE_URL =
  "https://tablet.sigwebtablet.com:47290/SigWeb/";

/**
 * Same listener addressed by loopback name. Kept as a fallback for locked-down
 * workstations that cannot resolve the public alias (no external DNS, no hosts
 * entry) AND have had a `localhost`-covering certificate installed in its
 * place. It fails cert validation on a stock install, which is why it is
 * second rather than first.
 */
export const SIGWEB_TLS_LOOPBACK_BASE_URL = "https://localhost:47290/SigWeb/";

/** Plaintext listener. Only reachable when the page itself is http (dev). */
export const SIGWEB_PLAINTEXT_BASE_URL = "http://localhost:47289/SigWeb/";

/** Kept for callers/tests that just want "the endpoint we prefer". */
export const SIGWEB_BASE_URL = SIGWEB_TLS_BASE_URL;

/** Probe budget. Kept short — it runs on dialog open, in front of a user. */
export const SIGWEB_DETECT_TIMEOUT_MS = 3000;
/** Budget for ordinary commands once the service is known to be up. */
export const SIGWEB_COMMAND_TIMEOUT_MS = 5000;

/**
 * SigWeb `JustifyMode`. Mode 5 trims surrounding whitespace and scales the ink
 * to fit the requested image box — the pad-side equivalent of the canvas path's
 * `getTrimmedCanvas()`, so both providers yield comparably cropped images.
 *
 * NOTE the name: the property is `JustifyMode`, NOT `ImageJustifyMode`. There
 * is no `ImageTransparency` property at all — see the wire-format note above.
 */
const JUSTIFY_TRIM_AND_FIT = 5;
const IMAGE_PEN_WIDTH = 2;

const TABLET_STATE_OFF = 0;
const TABLET_STATE_CAPTURING = 1;

/**
 * `SigImage/1` is the finished signature; `SigImage/0` is the in-progress frame
 * the vendor's own code polls to mirror ink onto a display canvas.
 */
const SIG_IMAGE_FINAL = "SigImage/1";
const SIG_IMAGE_PREVIEW = "SigImage/0";

/**
 * Why SigWeb could not be used, coarse enough for the UI to pick a remedy.
 *
 * - `unreachable`: nothing answered on loopback — not installed, service
 *   stopped, or the certificate no longer validates. Remedy: install/renew.
 * - `timeout`: the service exists but stalled. Remedy: check the cable, retry.
 * - `no-pad`: the service is running and answered, but no tablet is attached.
 *   Remedy: plug the pad in — installing SigWeb again fixes nothing.
 * - `error`: any other command failure.
 */
export type SigWebFailureKind = "unreachable" | "timeout" | "no-pad" | "error";

export class SigWebError extends Error {
  readonly kind: SigWebFailureKind;

  constructor(message: string, kind: SigWebFailureKind = "error") {
    super(message);
    this.name = "SigWebError";
    this.kind = kind;
  }
}

/**
 * Classify a failure so the desk can show the remedy that applies. A rejected
 * loopback fetch (bare `TypeError`) is "unreachable"; everything the client
 * threw on purpose carries its own kind.
 */
export function classifySigWebFailure(error: unknown): SigWebFailureKind {
  if (error instanceof DOMException && error.name === "AbortError") {
    return "timeout";
  }
  if (error instanceof SigWebError) {
    return error.kind;
  }
  return "unreachable";
}

const FAILURE_KIND_TO_DEVICE_FAILURE: Record<
  SigWebFailureKind,
  SignatureDeviceFailure
> = {
  unreachable: "service-unreachable",
  timeout: "timeout",
  "no-pad": "no-device",
  error: "error"
};

/** The same classification in the provider-agnostic vocabulary the desk renders. */
export function toDeviceFailure(error: unknown): SignatureDeviceFailure {
  return FAILURE_KIND_TO_DEVICE_FAILURE[classifySigWebFailure(error)];
}

/**
 * Turn a transport failure into a message a receptionist can act on.
 * A rejected fetch to the loopback listener is indistinguishable between "not
 * installed", "service stopped" and "certificate expired or not trusted", so
 * say all three — and name the URL that tells them which, since opening it in
 * a tab surfaces the real browser error (cert warning vs. connection refused).
 */
export function describeSigWebFailure(error: unknown): string {
  if (error instanceof DOMException && error.name === "AbortError") {
    return "The SigWeb service did not respond in time. Make sure the signature pad is plugged in, then try again.";
  }
  if (error instanceof SigWebError) {
    return error.message;
  }
  return `Could not reach the Topaz SigWeb service. Confirm SigWeb is installed and running on this computer, and that its security certificate is current — opening ${SIGWEB_TLS_BASE_URL}SigWebVersion in a new tab will show which.`;
}

/**
 * SigWeb is Windows software — Topaz publishes no macOS or Linux build — so on
 * any other platform the probe can only ever time out. Callers use this to skip
 * the probe and hide the pad entry points, rather than offering staff on a Mac
 * a "Try Again" that cannot succeed.
 *
 * `userAgentData.platform` where the browser has it; the UA string otherwise.
 * Chrome's UA reduction froze the platform token at "Windows NT 10.0", so the
 * fallback stays reliable. `userAgent` is injectable for tests (jsdom is not
 * Windows).
 */
export function isSigWebSupportedPlatform(
  userAgent: string = typeof navigator !== "undefined"
    ? navigator.userAgent
    : ""
): boolean {
  const platform =
    typeof navigator !== "undefined"
      ? (navigator as Navigator & { userAgentData?: { platform?: string } })
          .userAgentData?.platform
      : undefined;
  if (platform) return platform === "Windows";
  return /Windows/i.test(userAgent);
}

/** Protocol of the page we are running in; "https:" when there is no window. */
function getPageProtocol(): string {
  return typeof window !== "undefined"
    ? (window.location?.protocol ?? "https:")
    : "https:";
}

/**
 * Origins to try, best first. Mixed-content rules block a plaintext
 * subresource from an https:// page outright, so 47289 is only worth a probe
 * when the app itself is served over http (a local dev server).
 *
 * `pageProtocol` is injectable so both sides of that rule are testable — jsdom
 * always reports http, which is the branch production never takes.
 */
export function getSigWebBaseUrlCandidates(
  pageProtocol: string = getPageProtocol()
): string[] {
  const candidates = [SIGWEB_TLS_BASE_URL, SIGWEB_TLS_LOOPBACK_BASE_URL];
  if (pageProtocol === "http:") {
    candidates.push(SIGWEB_PLAINTEXT_BASE_URL);
  }
  return candidates;
}

interface SigWebSession {
  baseUrl: string;
  version: string;
}

/**
 * The endpoint that actually answered, remembered for the tab's lifetime so
 * the probe cost is paid once rather than on every command.
 */
let session: SigWebSession | null = null;

/** Test seam / recovery hook: forget the remembered endpoint. */
export function resetSigWebEndpoint(): void {
  session = null;
}

// Monotonic counter, not just a timestamp: two calls inside the same
// millisecond would otherwise share a URL and hit SigWeb's cache.
let cacheBusterSeq = 0;

const readText = (response: Response): Promise<string> =>
  response.text().then((text) => text.trim());
const readBlob = (response: Response): Promise<Blob> => response.blob();

interface SigWebRequestOptions<T> {
  /** Reads are GET; writes are POST. Defaults to GET. */
  method?: "GET" | "POST";
  timeoutMs?: number;
  /** Consumed inside the timeout window, so a stalled body still aborts. */
  read: (response: Response) => Promise<T>;
}

async function requestAt<T>(
  baseUrl: string,
  path: string,
  {
    method = "GET",
    timeoutMs = SIGWEB_COMMAND_TIMEOUT_MS,
    read
  }: SigWebRequestOptions<T>
): Promise<T> {
  const url = new URL(path, baseUrl);
  if (method === "GET") {
    // SigWeb caches GETs by URL; force a distinct URL per call. Writes are
    // POSTs and are not cached, and the vendor does not bust them either.
    cacheBusterSeq += 1;
    url.searchParams.set("noCache", `${Date.now()}-${cacheBusterSeq}`);
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url.toString(), {
      method,
      cache: "no-store",
      signal: controller.signal
    });
    if (!response.ok) {
      throw new SigWebError(`SigWeb returned ${response.status} for ${path}.`);
    }
    return await read(response);
  } catch (error) {
    // A rejected loopback fetch surfaces to the user as one generic sentence,
    // because the browser hides WHY (cert, refused, blocked). The console is
    // the only place the failing request itself can be named, and it is what
    // support asks for first.
    console.warn(
      `SigWeb: ${method} ${url.origin}/SigWeb/${path} failed.`,
      error
    );
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Probe every candidate origin at once and keep the first that answers.
 *
 * Concurrent rather than sequential on purpose: tried in turn, a workstation
 * with no service at all would burn `SIGWEB_DETECT_TIMEOUT_MS` per candidate
 * before the dialog could say anything. In parallel the whole probe still
 * costs one timeout.
 */
async function connect(): Promise<SigWebSession> {
  if (session) return session;

  const candidates = getSigWebBaseUrlCandidates();
  try {
    session = await Promise.any(
      candidates.map(async (baseUrl) => ({
        baseUrl,
        version: await requestAt(baseUrl, "SigWebVersion", {
          timeoutMs: SIGWEB_DETECT_TIMEOUT_MS,
          read: readText
        })
      }))
    );
  } catch (error) {
    // Report the preferred candidate's failure — it is the one whose remedy
    // (install SigWeb / renew its certificate) the user can actually act on.
    if (error instanceof AggregateError && error.errors.length > 0) {
      throw error.errors[0];
    }
    throw error;
  }
  return session;
}

/** `GET <name>` — reads a property, or invokes a side-effecting one. */
async function readProperty(name: string): Promise<string> {
  const { baseUrl } = await connect();
  return requestAt(baseUrl, name, { read: readText });
}

/** `POST <name>/<value>` — the only shape SigWeb accepts as a write. */
async function writeProperty(
  name: string,
  value: string | number
): Promise<void> {
  const { baseUrl } = await connect();
  await requestAt(baseUrl, `${name}/${value}`, {
    method: "POST",
    read: readText
  });
}

/**
 * Returns the SigWeb service version, or throws if it cannot be reached.
 *
 * Always hits the wire, even when an endpoint is already remembered: this is
 * the liveness probe the dialog opens with, and a cached answer would report a
 * pad that has since been unplugged as present.
 */
export async function getSigWebVersion(): Promise<string> {
  if (session) {
    try {
      return await requestAt(session.baseUrl, "SigWebVersion", {
        timeoutMs: SIGWEB_DETECT_TIMEOUT_MS,
        read: readText
      });
    } catch {
      // The remembered endpoint stopped answering (service restarted, cert
      // rotated, machine moved networks). Re-probe rather than declaring the
      // pad missing on the strength of a stale choice.
      session = null;
    }
  }
  return (await connect()).version;
}

/**
 * Image-shape settings. Cosmetic by nature: getting one wrong yields a
 * differently sized or cropped signature, never an unusable one.
 *
 * They are therefore best-effort. A vendor rename here previously took the
 * WHOLE dialog down — `ImageTransparency` 404'd and nobody could sign at all —
 * which is a wildly disproportionate outcome for a pen-width setting. Failures
 * are warned about, not thrown. Arming and clearing the pad below stay fatal.
 */
async function applyImageSettings(
  width: number,
  height: number
): Promise<void> {
  const settings: [string, number][] = [
    ["ImageXSize", Math.round(width)],
    ["ImageYSize", Math.round(height)],
    // The live mirror (`SigImage/0`) is rendered at the DISPLAY size, which
    // the vendor sets to its preview canvas before arming. Matching it to the
    // desk box means strokes land where the signer expects them.
    ["DisplayXSize", Math.round(width)],
    ["DisplayYSize", Math.round(height)],
    ["ImagePenWidth", IMAGE_PEN_WIDTH],
    ["JustifyMode", JUSTIFY_TRIM_AND_FIT]
  ];

  for (const [name, value] of settings) {
    try {
      await writeProperty(name, value);
    } catch (error) {
      console.warn(
        `SigWeb: could not set ${name}; using the pad default.`,
        error
      );
    }
  }
}

/**
 * Confirm the pad actually opened.
 *
 * `SigWebVersion` answers whenever the SERVICE is running, pad or no pad, so
 * detection alone cannot tell "ready to sign" from "no hardware attached" —
 * which is how the dialog came to say "Sign on the Topaz pad" at a workstation
 * with nothing plugged in. Writing `TabletState/1` does not fail in that case
 * either; the state simply stays 0. Reading it back is the vendor's own test
 * (`SetTabletState` checks `GetTabletState()` after arming).
 *
 * A value we cannot parse is NOT treated as a failure: blocking a working pad
 * because some firmware answers in an unexpected shape is worse than the
 * vague prompt this replaces.
 */
async function assertPadIsOpen(): Promise<void> {
  let raw: string;
  try {
    raw = await readProperty("TabletState");
  } catch (error) {
    console.warn("SigWeb: could not read back TabletState.", error);
    return;
  }

  const state = Number.parseInt(raw, 10);
  if (Number.isFinite(state) && state !== TABLET_STATE_CAPTURING) {
    throw new SigWebError(
      "SigWeb is running, but no signature pad is responding. Check that the pad is plugged into this computer, then try again.",
      "no-pad"
    );
  }
}

/** Configure the image the service will hand back, then arm the pad for ink. */
export async function startCapture(
  width: number,
  height: number
): Promise<void> {
  await applyImageSettings(width, height);
  // Fatal from here down: a stale signature from the previous signer must
  // never survive into this capture, and an unarmed pad cannot take ink.
  await clearSignature();
  await writeProperty("TabletState", TABLET_STATE_CAPTURING);
  await assertPadIsOpen();
}

/** Wipe the pad, leaving the session open for a fresh attempt. */
export async function clearSignature(): Promise<void> {
  await readProperty("ClearSignature");
}

/** Release the pad. Swallows errors — teardown must never surface to the user. */
export async function stopCapture(): Promise<void> {
  // Never probe on teardown: the detect effect's cleanup also runs after a
  // failed detection, and firing a fresh round of 3s probes at a service that
  // was just shown to be absent buys nothing.
  if (!session) return;
  try {
    await writeProperty("TabletState", TABLET_STATE_OFF);
  } catch {
    // Best-effort: the dialog is closing and the session dies with the pad anyway.
  }
}

/**
 * Number of captured pen points. 0 means the signer hasn't touched the pad.
 * The property is `TotalPoints` — `NumberOfTabletPoints` is the vendor's JS
 * FUNCTION name, not the endpoint it calls.
 */
export async function getPointCount(): Promise<number> {
  const raw = await readProperty("TotalPoints");
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/**
 * The attached pad's model string, or `null` when SigWeb will not say.
 *
 * Best-effort by construction, and deliberately so. Everything this returns is
 * for a RUM dimension and a line of support copy — nobody signs any differently
 * because of it — whereas a wrong property name here is a 404 (see
 * `ImageTransparency` in the header note) that would otherwise take the whole
 * dialog down. So the failure mode is a missing label, never a broken capture.
 *
 * The endpoint name is verified against the vendor wrapper: its
 * `TabletModelNumber()` function reads `GET TabletModelNumber`. What a real
 * pad puts in the response body is not — confirm the value on the physical
 * T-LBK460 and delete this sentence once RUM shows a non-null model.
 */
export async function getTabletModel(): Promise<string | null> {
  try {
    const model = await readProperty("TabletModelNumber");
    return model || null;
  } catch {
    return null;
  }
}

/**
 * The finished signature as image bytes.
 *
 * SigWeb serves an encoded image here, NOT base64 text, and does not document
 * which format — the vendor's own code just hands the blob to an `<img>`. The
 * provider re-encodes it to PNG through a canvas for that reason.
 */
export async function getSignatureImageBlob(): Promise<Blob> {
  const { baseUrl } = await connect();
  return requestAt(baseUrl, SIG_IMAGE_FINAL, { read: readBlob });
}

/**
 * The in-progress ink as an image, for mirroring strokes on screen while the
 * signer is still signing. Same undocumented format as the final image; the
 * vendor's `SigWebRefresh` hands it straight to an `<img>`, and so does the
 * desk. Rendered at `DisplayXSize` × `DisplayYSize`.
 *
 * Cosmetic: a failure here costs the live preview, never the signature, so
 * callers treat a rejection as "no frame this tick".
 */
export async function getInkPreviewBlob(): Promise<Blob> {
  const { baseUrl } = await connect();
  return requestAt(baseUrl, SIG_IMAGE_PREVIEW, { read: readBlob });
}
